import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { SidebarBrand, SwitchAppList } from "@/components/AppSidebar";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Home as HomeIcon, Bell, Mail, User, LogOut, ChevronDown, LayoutGrid,
  BookOpen, Users, Search, Send, PenSquare, X, ArrowLeft,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { formatDistanceToNow, format, isToday, isYesterday } from "date-fns";

const getInitials = (name) =>
  name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "?";

const msgTime = (iso) => {
  try {
    const d = new Date(iso);
    if (isToday(d)) return format(d, "h:mm a");
    if (isYesterday(d)) return "Yesterday";
    return format(d, "MMM d");
  } catch { return ""; }
};

const fullTime = (iso) => {
  try { return format(new Date(iso), "MMM d, h:mm a"); } catch { return ""; }
};

// ──────────────────────────────────────────────
// Shared Sidebar (same structure as Community.jsx)
// ──────────────────────────────────────────────
function CommunitySidebar({ user, navigate, notifBadge = 0, dmBadge = 0 }) {
  const NavItem = ({ icon: Icon, label, onClick, active = false, badge = 0 }) => (
    <button
      data-testid={`community-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
      onClick={onClick}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors w-full text-left group relative ${
        active ? "bg-amber-50 text-amber-700 font-semibold" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      }`}
    >
      <div className="relative">
        <Icon className={`w-[18px] h-[18px] shrink-0 ${active ? "text-amber-600" : "text-slate-400 group-hover:text-slate-600"}`} />
        {badge > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-3.5 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center px-0.5">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </div>
      <span className="text-sm hidden xl:block">{label}</span>
    </button>
  );

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`); } finally { navigate("/"); }
  };

  return (
    <aside className="hidden md:flex flex-col fixed top-0 left-0 h-screen w-[68px] xl:w-[260px] shrink-0 bg-white border-r border-slate-200 px-2 xl:px-4 py-4 overflow-y-auto z-40">
      {/* App Switcher */}
      <div className="h-16 shrink-0 flex items-center px-1 mb-1">
        <SidebarBrand brandLabel="Community" />
      </div>

      <SwitchAppList user={user} active="community" />

      <Separator className="mb-2" />

      <p className="px-2.5 mb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider hidden xl:block">
        Current App
      </p>

      <nav className="flex flex-col gap-0.5 flex-1">
        <NavItem icon={HomeIcon}   label="Home"          onClick={() => navigate("/community")} />
        <NavItem icon={Bell}       label="Notifications" onClick={() => navigate("/community/notifications")} badge={notifBadge} />
        <NavItem icon={Mail}       label="Messages"      onClick={() => navigate("/community/messages")} active badge={0} />
        <NavItem icon={User}       label="Profile"       onClick={() => navigate("/community/profile")} />
      </nav>

      <div className="my-2 border-t border-slate-100" />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="community-user-menu"
            className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-100 transition-colors w-full">
            <Avatar className="h-8 w-8 shrink-0">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-amber-100 text-amber-700 text-xs font-semibold">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="hidden xl:block text-left min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="top" className="w-52">
          <DropdownMenuItem onClick={() => navigate("/community/profile")} className="gap-2">
            <User className="w-4 h-4" />My Profile
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleLogout} className="gap-2 text-red-600 focus:text-red-700 focus:bg-red-50">
            <LogOut className="w-4 h-4" />Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </aside>
  );
}

// ──────────────────────────────────────────────
// Conversation List Item
// ──────────────────────────────────────────────
function ConvItem({ conv, isActive, onClick }) {
  return (
    <button
      data-testid={`conv-item-${conv.conversation_id}`}
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3.5 border-b border-slate-100 transition-colors text-left hover:bg-slate-50 ${
        isActive ? "bg-amber-50/60 border-l-2 border-l-amber-500" : ""
      }`}
    >
      <div className="relative shrink-0">
        <Avatar className="h-11 w-11">
          <AvatarImage src={conv.other_user?.picture} />
          <AvatarFallback className="bg-slate-100 text-slate-600 text-sm font-semibold">
            {getInitials(conv.other_user?.name)}
          </AvatarFallback>
        </Avatar>
        {conv.unread_count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center">
            {conv.unread_count > 9 ? "9+" : conv.unread_count}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-1">
          <p className={`text-sm truncate ${conv.unread_count > 0 ? "font-bold text-slate-900" : "font-medium text-slate-800"}`}>
            {conv.other_user?.name}
          </p>
          {conv.last_message && (
            <span className="text-[11px] text-slate-400 shrink-0">
              {msgTime(conv.last_message.created_at)}
            </span>
          )}
        </div>
        {conv.last_message && (
          <p className={`text-xs truncate mt-0.5 ${conv.unread_count > 0 ? "text-slate-700 font-medium" : "text-slate-400"}`}>
            {conv.last_message.sender_id === conv.other_user?.user_id ? "" : "You: "}
            {conv.last_message.content}
          </p>
        )}
      </div>
    </button>
  );
}

// ──────────────────────────────────────────────
// Message Bubble
// ──────────────────────────────────────────────
function MessageBubble({ msg, isMe }) {
  return (
    <div className={`flex items-end gap-2 mb-1 ${isMe ? "flex-row-reverse" : "flex-row"}`}>
      {!isMe && (
        <Avatar className="h-7 w-7 shrink-0 mb-0.5">
          <AvatarImage src={msg.sender_picture} />
          <AvatarFallback className="bg-slate-100 text-slate-600 text-[10px] font-semibold">
            {getInitials(msg.sender_name)}
          </AvatarFallback>
        </Avatar>
      )}
      <div className={`group max-w-[65%] ${isMe ? "items-end" : "items-start"} flex flex-col`}>
        <div
          className={`px-3.5 py-2 rounded-2xl text-sm leading-relaxed ${
            isMe
              ? "bg-amber-500 text-white rounded-br-md"
              : "bg-slate-100 text-slate-800 rounded-bl-md"
          }`}
        >
          {msg.content}
        </div>
        <span className="text-[10px] text-slate-400 mt-0.5 px-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {fullTime(msg.created_at)}
        </span>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// New Message Modal
// ──────────────────────────────────────────────
function NewMessageModal({ open, onClose, onSelect }) {
  const [members, setMembers] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (open) {
      axios.get(`${API}/feed/community/members`)
        .then((r) => setMembers(r.data))
        .catch(() => {});
    }
  }, [open]);

  const filtered = query
    ? members.filter((m) => m.name.toLowerCase().includes(query.toLowerCase()))
    : members;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-sm p-0">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
          <DialogTitle className="text-slate-900 font-semibold">New Message</DialogTitle>
          <DialogDescription className="sr-only">Select a community member to start a conversation</DialogDescription>
        </DialogHeader>
        <div className="px-4 py-2 border-b border-slate-100">
          <div className="flex items-center gap-2 bg-slate-50 rounded-full px-3 py-2">
            <Search className="w-4 h-4 text-slate-400 shrink-0" />
            <input
              autoFocus
              data-testid="new-msg-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search community…"
              className="flex-1 bg-transparent text-sm text-slate-900 placeholder-slate-400 outline-none"
            />
            {query && (
              <button onClick={() => setQuery("")} className="text-slate-400 hover:text-slate-600">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
        <div className="overflow-y-auto max-h-72">
          {filtered.length === 0 ? (
            <p className="text-center text-slate-400 text-sm py-8">No members found</p>
          ) : (
            filtered.map((m) => (
              <button
                key={m.user_id}
                data-testid={`new-msg-user-${m.user_id}`}
                onClick={() => onSelect(m)}
                className="flex items-center gap-3 w-full px-5 py-3 hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0"
              >
                <Avatar className="h-9 w-9 shrink-0">
                  <AvatarImage src={m.picture} />
                  <AvatarFallback className="bg-slate-100 text-slate-600 text-xs font-semibold">
                    {getInitials(m.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="text-left">
                  <p className="text-sm font-medium text-slate-900">{m.name}</p>
                  <p className="text-xs text-slate-400 capitalize">{m.role}</p>
                </div>
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────
// Main Component
// ──────────────────────────────────────────────
export default function CommunityMessages({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selectedConvId, setSelectedConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [convSearch, setConvSearch] = useState("");
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showNewMsg, setShowNewMsg] = useState(false);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const inputRef = useRef(null);

  const selectedConv = conversations.find((c) => c.conversation_id === selectedConvId);

  // On mount: fetch conversations, open if query param
  useEffect(() => {
    fetchConversations();
    const convId = searchParams.get("conv");
    if (convId) setSelectedConvId(convId);
  }, []);

  // When conversation selected: fetch messages + start polling
  useEffect(() => {
    if (!selectedConvId) return;
    fetchMessages(selectedConvId);
    // Poll for new messages every 4s
    pollRef.current = setInterval(() => fetchMessages(selectedConvId, true), 4000);
    return () => clearInterval(pollRef.current);
  }, [selectedConvId]);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const { data } = await axios.get(`${API}/community/messages/conversations`);
      setConversations(data);
    } catch { /* silent */ }
  };

  const fetchMessages = async (convId, silent = false) => {
    try {
      const { data } = await axios.get(`${API}/community/messages/conversations/${convId}/messages`);
      setMessages(data);
      // Update unread badge in conversations list
      setConversations((prev) =>
        prev.map((c) => c.conversation_id === convId ? { ...c, unread_count: 0 } : c)
      );
    } catch { if (!silent) toast.error("Failed to load messages"); }
  };

  const handleSelectConv = (conv) => {
    setSelectedConvId(conv.conversation_id);
    setMobileShowChat(true);
    inputRef.current?.focus();
  };

  const handleStartNew = async (member) => {
    setShowNewMsg(false);
    try {
      const { data } = await axios.post(`${API}/community/messages/conversations`, {
        recipient_id: member.user_id,
      });
      // Add to list if not already there
      setConversations((prev) => {
        const exists = prev.find((c) => c.conversation_id === data.conversation_id);
        return exists ? prev : [data, ...prev];
      });
      setSelectedConvId(data.conversation_id);
      setMobileShowChat(true);
    } catch {
      toast.error("Failed to start conversation");
    }
  };

  const handleSend = async () => {
    if (!input.trim() || !selectedConvId || sending) return;
    const content = input.trim();
    setInput("");
    setSending(true);
    // Optimistic update
    const tempMsg = {
      message_id: `temp_${Date.now()}`,
      conversation_id: selectedConvId,
      sender_id: user.user_id,
      sender_name: user.name,
      sender_picture: user.picture,
      content,
      read: false,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempMsg]);
    try {
      const { data } = await axios.post(
        `${API}/community/messages/conversations/${selectedConvId}/messages`,
        { content }
      );
      setMessages((prev) => prev.map((m) => m.message_id === tempMsg.message_id ? data : m));
      // Refresh conversations for updated last_message
      fetchConversations();
    } catch {
      toast.error("Failed to send");
      setMessages((prev) => prev.filter((m) => m.message_id !== tempMsg.message_id));
    } finally {
      setSending(false);
    }
  };

  const filteredConvs = convSearch
    ? conversations.filter((c) =>
        c.other_user?.name?.toLowerCase().includes(convSearch.toLowerCase())
      )
    : conversations;

  // Group messages by date
  const groupedMessages = messages.reduce((acc, msg) => {
    const day = format(new Date(msg.created_at), "yyyy-MM-dd");
    if (!acc[day]) acc[day] = [];
    acc[day].push(msg);
    return acc;
  }, {});

  const dayLabel = (dateStr) => {
    const d = new Date(dateStr);
    if (isToday(d)) return "Today";
    if (isYesterday(d)) return "Yesterday";
    return format(d, "MMMM d, yyyy");
  };

  return (
    <div className="h-screen overflow-hidden bg-slate-50 flex">
      {/* Left nav sidebar */}
      <CommunitySidebar user={user} navigate={navigate} />

      {/* Messages area */}
      <div className="flex flex-1 md:ml-[68px] xl:ml-[260px] justify-center overflow-hidden">
        <div className="flex w-full max-w-5xl h-full overflow-hidden">
        <div className="flex flex-1 min-w-0 border-x border-slate-200 bg-white h-full overflow-hidden">

          {/* ── Conversation List Panel ── */}
          <div className={`
            flex flex-col border-r border-slate-200 bg-white h-full
            w-full md:w-[320px] lg:w-[360px] shrink-0
            ${mobileShowChat ? "hidden md:flex" : "flex"}
          `}>
            {/* Header */}
            <div className="px-4 py-3.5 border-b border-slate-100 flex items-center justify-between shrink-0">
              <h1 className="font-serif font-bold text-slate-900 text-lg">Messages</h1>
              <button
                data-testid="new-message-btn"
                onClick={() => setShowNewMsg(true)}
                className="p-2 rounded-full hover:bg-slate-100 transition-colors text-slate-600 hover:text-amber-600"
                title="New message"
              >
                <PenSquare className="w-5 h-5" />
              </button>
            </div>

            {/* Search */}
            <div className="px-3 py-2 border-b border-slate-100 shrink-0">
              <div className="flex items-center gap-2 bg-slate-50 rounded-full px-3 py-2">
                <Search className="w-4 h-4 text-slate-400 shrink-0" />
                <input
                  data-testid="conv-search-input"
                  value={convSearch}
                  onChange={(e) => setConvSearch(e.target.value)}
                  placeholder="Search messages…"
                  className="flex-1 bg-transparent text-sm text-slate-900 placeholder-slate-400 outline-none"
                />
                {convSearch && (
                  <button onClick={() => setConvSearch("")} className="text-slate-400 hover:text-slate-600">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Conversation list */}
            <div className="flex-1 overflow-y-auto" data-testid="conversations-list">
              {filteredConvs.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 px-6 text-center">
                  <Mail className="w-8 h-8 text-slate-200 mb-3" />
                  <p className="text-sm text-slate-500 font-medium">No conversations yet</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Tap the pencil icon to message someone
                  </p>
                </div>
              ) : (
                filteredConvs.map((conv) => (
                  <ConvItem
                    key={conv.conversation_id}
                    conv={conv}
                    isActive={selectedConvId === conv.conversation_id}
                    onClick={() => handleSelectConv(conv)}
                  />
                ))
              )}
            </div>
          </div>

          {/* ── Chat Thread Panel ── */}
          <div className={`
            flex-1 flex flex-col min-w-0 bg-white h-full overflow-hidden
            ${!mobileShowChat && selectedConvId === null ? "hidden md:flex" : "flex"}
          `}>
            {!selectedConv ? (
              /* Empty state */
              <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                  <Mail className="w-8 h-8 text-slate-300" />
                </div>
                <h2 className="font-serif text-lg font-semibold text-slate-800 mb-1">
                  Your messages
                </h2>
                <p className="text-slate-400 text-sm max-w-xs">
                  Send private messages to anyone in the Community.
                </p>
                <Button
                  data-testid="empty-new-message-btn"
                  onClick={() => setShowNewMsg(true)}
                  className="mt-5 bg-amber-500 hover:bg-amber-600 text-white rounded-full px-5"
                >
                  New Message
                </Button>
              </div>
            ) : (
              <>
                {/* Chat header */}
                <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3 shrink-0 bg-white/90 backdrop-blur-sm">
                  <button
                    className="md:hidden p-1 rounded-full hover:bg-slate-100 text-slate-500"
                    onClick={() => { setMobileShowChat(false); setSelectedConvId(null); }}
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <Avatar className="h-9 w-9 shrink-0">
                    <AvatarImage src={selectedConv.other_user?.picture} />
                    <AvatarFallback className="bg-slate-100 text-slate-600 text-xs font-semibold">
                      {getInitials(selectedConv.other_user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm leading-none">
                      {selectedConv.other_user?.name}
                    </p>
                    <p className="text-xs text-slate-400 capitalize mt-0.5">
                      {selectedConv.other_user?.role}
                    </p>
                  </div>
                </div>

                {/* Messages */}
                <div
                  data-testid="messages-thread"
                  className="flex-1 overflow-y-auto px-4 py-4 space-y-1"
                >
                  {Object.entries(groupedMessages).map(([day, msgs]) => (
                    <div key={day}>
                      <div className="flex items-center gap-3 my-4">
                        <div className="flex-1 h-px bg-slate-100" />
                        <span className="text-[11px] text-slate-400 font-medium">{dayLabel(day)}</span>
                        <div className="flex-1 h-px bg-slate-100" />
                      </div>
                      {msgs.map((msg) => (
                        <MessageBubble
                          key={msg.message_id}
                          msg={msg}
                          isMe={msg.sender_id === user.user_id}
                        />
                      ))}
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input bar */}
                <div className="px-4 py-3 border-t border-slate-100 bg-white shrink-0">
                  <div className="flex items-end gap-2">
                    <div className="flex-1 bg-slate-50 border border-slate-200 rounded-2xl px-4 py-2.5 focus-within:border-amber-400 focus-within:ring-2 focus-within:ring-amber-400/20 transition-all">
                      <textarea
                        ref={inputRef}
                        data-testid="message-input"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSend();
                          }
                        }}
                        placeholder="Start a new message"
                        rows={1}
                        className="w-full bg-transparent text-sm text-slate-900 placeholder-slate-400 outline-none resize-none max-h-24"
                      />
                    </div>
                    <Button
                      data-testid="send-message-btn"
                      onClick={handleSend}
                      disabled={!input.trim() || sending}
                      className="h-10 w-10 p-0 rounded-full bg-amber-500 hover:bg-amber-600 text-white shrink-0 shadow-sm disabled:opacity-40"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1.5 px-1">
                    Press <kbd className="font-mono">Enter</kbd> to send · <kbd className="font-mono">Shift+Enter</kbd> for new line
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
        </div>{/* max-w-5xl wrapper */}
      </div>{/* ml offset wrapper */}

      {/* New Message modal */}
      <NewMessageModal
        open={showNewMsg}
        onClose={() => setShowNewMsg(false)}
        onSelect={handleStartNew}
      />

      {/* Mobile bottom bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 flex items-center justify-around px-4 py-2 z-50">
        <button onClick={() => navigate("/community")} className="p-2 text-slate-400">
          <HomeIcon className="w-6 h-6" />
        </button>
        <button onClick={() => navigate("/community/notifications")} className="p-2 text-slate-400">
          <Bell className="w-6 h-6" />
        </button>
        <button className="p-2 text-amber-600">
          <Mail className="w-6 h-6" />
        </button>
        <button onClick={() => navigate("/community/profile")} className="p-2 text-slate-400">
          <User className="w-6 h-6" />
        </button>
      </nav>
    </div>
  );
}
